// apps/web/src/pages/admin/reservations/ReservationsPage.tsx — Phase 5 plan 05-04
// (B4) + Quick 260803-ger (D-01..D-07).
//
// Merged admin "Reservierungen" surface at /admin/reservierungen. The
// Heute presentation (capacity bar, list/timeline toggle, sort chips,
// card row) and the Reservierungen operations (search, status/source
// filter, infinite pagination, edit + cancel) now live on the same
// page. Calendar features that survived:
//   - Collapsible month-grid date picker with the 5 day-state classes
//     (PAST / AVAILABLE / FULL / OUTSIDE_WINDOW / BLOCKED).
//   - Arrow buttons for ±1 day.
//   - Explicit "Tag sperren" action that opens CalendarDayTapDialog.
//
// Search scope: with no search text, the GraphQL filter is date-bounded
// (`fromDate = toDate = selected date`). With non-empty search text,
// the date bounds are dropped and a localized notice + reset action
// are shown — staff need to find a guest by name without knowing the
// date.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatInTimeZone } from "date-fns-tz";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../../lib/api";
import { useInvalidateAdminCaches } from "../../../lib/admin-cache";
import {
  RESERVATIONS_PAGINATED_QUERY,
  RESERVATION_CANCEL_MUTATION,
  RESERVATION_SET_STATUS_MUTATION,
  RESERVATION_MARK_ARRIVED_MUTATION,
  RESERVATION_MARK_NO_SHOW_MUTATION,
} from "./reservations-operations";
import {
  ReservationRowActions,
  type ReservationRowItem,
} from "./ReservationRow";
import { ReservationsDatePicker } from "./ReservationsDatePicker";
import {
  ALLOWED_SOURCE,
  ALLOWED_STATUS,
  ReservationFilters,
  type ReservationFilterValue,
  type ReservationSourceFilter,
  type ReservationStatusFilter,
} from "./ReservationFilters";
import { ExportButton } from "./ExportButton";
import {
  TodayListView,
  type TodayReservationItem,
} from "../today/TodayListView";
import { TodayTimelineView } from "../today/TodayTimelineView";
import { type TodaySortKey } from "../today/TodaySortChips";
import {
  CalendarDayTapDialog,
  type DayStateKind,
} from "../calendar/CalendarDayTapDialog";
import { TODAY_RESERVATIONS_QUERY } from "../today/today-operations";
import { AVAILABILITY_MONTH_QUERY } from "../calendar/calendar-operations";

const LAYOUT_STORAGE_KEY = "gastio.reservations.layout";
type Layout = "list" | "timeline";

function readStoredLayout(): Layout {
  if (typeof window === "undefined") return "list";
  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (stored === "timeline") return "timeline";
    return "list";
  } catch {
    return "list";
  }
}

function parseStatus(value: string | null): ReservationStatusFilter[] {
  return (value?.split(",") ?? [])
    .map((item) => item.trim())
    .filter((item): item is ReservationStatusFilter =>
      (ALLOWED_STATUS as readonly string[]).includes(item),
    );
}

function parseSource(value: string | null): ReservationSourceFilter[] {
  return (value?.split(",") ?? [])
    .map((item) => item.trim())
    .filter((item): item is ReservationSourceFilter =>
      (ALLOWED_SOURCE as readonly string[]).includes(item),
    );
}

type ListParamsPatch = Partial<{
  date: string;
  search: string;
  status: ReservationStatusFilter[];
  source: ReservationSourceFilter[];
}>;

export function ReservationsPage() {
  const { t } = useTranslation();
  const invalidate = useInvalidateAdminCaches();
  const [searchParams, setSearchParams] = useSearchParams();

  // Today-anchor via Europe/Berlin — never UTC slice.
  const todayBerlin = useMemo(
    () => formatInTimeZone(new Date(), "Europe/Berlin", "yyyy-MM-dd"),
    [],
  );
  const date = searchParams.get("date") ?? todayBerlin;
  const search = searchParams.get("search") ?? "";
  const statusParam = searchParams.get("status");
  const sourceParam = searchParams.get("source");
  const filter = useMemo<ReservationFilterValue>(
    () => ({
      status: parseStatus(statusParam),
      source: parseSource(sourceParam),
    }),
    [sourceParam, statusParam],
  );
  const hasExplicitFilter = ["date", "search", "status", "source"].some((key) =>
    searchParams.has(key),
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [pendingCancel, setPendingCancel] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<string | null>(null);
  const [pendingArrived, setPendingArrived] = useState<string | null>(null);
  const [pendingNoShow, setPendingNoShow] = useState<string | null>(null);
  const [layout, setLayout] = useState<Layout>(readStoredLayout);
  const [sortKey, setSortKey] = useState<TodaySortKey>("time");
  const [blockDayDialog, setBlockDayDialog] = useState<{
    date: string;
    dayState: DayStateKind;
  } | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
    } catch {
      /* localStorage may be unavailable */
    }
  }, [layout]);

  const setListParams = (patch: ListParamsPatch) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);

        if ("date" in patch) {
          if (patch.date) next.set("date", patch.date);
          else next.delete("date");
        }
        if ("search" in patch) {
          if (patch.search) next.set("search", patch.search);
          else next.delete("search");
        }
        if ("status" in patch) {
          if (patch.status?.length) next.set("status", patch.status.join(","));
          else next.delete("status");
        }
        if ("source" in patch) {
          if (patch.source?.length) next.set("source", patch.source.join(","));
          else next.delete("source");
        }

        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Search scope: empty search → date-bounded. Non-empty search → drop
  // date bounds and show a notice + reset action. Status/source
  // filters still apply either way.
  const searchActive = debouncedSearch.length > 0;
  const queryFilter = useMemo(
    () => ({
      ...(searchActive ? {} : { fromDate: date, toDate: date }),
      search: debouncedSearch || null,
      status: filter.status,
      source: filter.source,
    }),
    [date, debouncedSearch, filter, searchActive],
  );

  const query = useInfiniteQuery({
    queryKey: ["reservations", queryFilter],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const res = await gqlRequest<{
        reservations: {
          items: ReservationRowItem[];
          nextCursor: string | null;
        };
      }>(RESERVATIONS_PAGINATED_QUERY, {
        filter: queryFilter,
        cursor: pageParam,
        limit: 20,
      });
      return res.reservations;
    },
    getNextPageParam: (last) => last.nextCursor,
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await gqlRequest<{
        cancelReservationByAdmin: { id: string; status: string };
      }>(RESERVATION_CANCEL_MUTATION, { id, reason: null });
      return res.cancelReservationByAdmin;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  // Quick 260831-q3h: per-status inline actions. Confirm uses the
  // catch-all setReservationStatus (ANGEFRAGT → BESTAETIGT). Arrived /
  // no-show use the dedicated mutations so the server-side audit row
  // carries the right marker (Phase 5 D-07).
  const confirmMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await gqlRequest<{
        setReservationStatus: { id: string; status: string };
      }>(RESERVATION_SET_STATUS_MUTATION, { id, status: "BESTAETIGT" });
      return res.setReservationStatus;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  const markArrivedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await gqlRequest<{
        markArrived: { id: string; status: string };
      }>(RESERVATION_MARK_ARRIVED_MUTATION, { id });
      return res.markArrived;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  const markNoShowMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await gqlRequest<{
        markNoShow: { id: string; status: string };
      }>(RESERVATION_MARK_NO_SHOW_MUTATION, { id });
      return res.markNoShow;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  // Mount a dedicated full-day fetch for the timeline view.
  const timelineQuery = useQuery({
    queryKey: ["reservations", "timeline", date],
    queryFn: async () => {
      const res = await gqlRequest<{
        reservations: {
          items: TodayReservationItem[];
          nextCursor: string | null;
        };
      }>(TODAY_RESERVATIONS_QUERY, { today: date });
      return res.reservations.items;
    },
    enabled: layout === "timeline",
  });

  // The list/timeline is rendered as a single block; the rows below
  // (edit + cancel actions) are part of the list path only.
  const items: ReservationRowItem[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  // For the list layout, the TodayListView expects TodayReservationItem;
  // the two shapes are identical so we just cast through the shared keys.
  const listViewItems: TodayReservationItem[] = useMemo(
    () => items.map((i) => ({ ...i }) as TodayReservationItem),
    [items],
  );

  // Day-state lookup for the explicit "Tag sperren" action.
  const month = useMemo(() => date.slice(0, 7), [date]);
  const availabilityQuery = useQuery({
    queryKey: ["reservations", "availability", month],
    queryFn: async () => {
      const res = await gqlRequest<{
        availability: Array<{
          date: string;
          state: DayStateKind;
          reasons: string[];
        }>;
      }>(AVAILABILITY_MONTH_QUERY, { month });
      return res.availability;
    },
  });
  const selectedDayState: DayStateKind | null = useMemo(() => {
    const cell = availabilityQuery.data?.find((c) => c.date === date);
    return cell?.state ?? null;
  }, [availabilityQuery.data, date]);

  const onCancel = (id: string) => {
    setPendingCancel(id);
    cancelMutation.mutate(id, {
      onSettled: () => setPendingCancel(null),
    });
  };

  const onConfirm = (id: string) => {
    setPendingConfirm(id);
    confirmMutation.mutate(id, {
      onSettled: () => setPendingConfirm(null),
    });
  };

  const onMarkArrived = (id: string) => {
    setPendingArrived(id);
    markArrivedMutation.mutate(id, {
      onSettled: () => setPendingArrived(null),
    });
  };

  const onMarkNoShow = (id: string) => {
    setPendingNoShow(id);
    markNoShowMutation.mutate(id, {
      onSettled: () => setPendingNoShow(null),
    });
  };

  const clearSearchAndReturn = () => {
    setListParams({ search: "" });
  };

  const openTagSperren = () => {
    if (!selectedDayState) return;
    setBlockDayDialog({ date, dayState: selectedDayState });
  };

  const manualCreateHref = `/admin/reservierungen/neu?date=${date}`;

  return (
    <div className="px-4 py-6" data-testid="reservations-page">
      <header className="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <h1 className="font-heading text-2xl">
          {t("admin.reservations.pageTitle")}
        </h1>
        <Link
          to={manualCreateHref}
          className="btn btn-primary"
          data-testid="reservations-header-manual-create"
        >
          {t("admin.reservations.header.manualCreate")}
        </Link>
      </header>

      <ReservationsDatePicker
        date={date}
        onChange={(next) => setListParams({ date: next })}
        onOpenTagSperren={openTagSperren}
        initiallyOpen={false}
      />

      {searchActive && (
        <div
          className="card p-3 mb-4 flex items-center justify-between gap-2"
          role="status"
          data-testid="reservations-search-scope-notice"
        >
          <p className="text-sm">
            {t("admin.reservations.search.scopeNotice", { date })}
          </p>
          <button
            type="button"
            className="btn btn-outline"
            onClick={clearSearchAndReturn}
            data-testid="reservations-search-scope-reset"
          >
            {t("admin.reservations.search.scopeReset")}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div
          className="inline-flex rounded-full border border-divider overflow-hidden"
          role="group"
          aria-label={t("admin.today.layout.label")}
          data-testid="reservations-layout-toggle"
        >
          <button
            type="button"
            aria-pressed={layout === "list"}
            onClick={() => setLayout("list")}
            className={`px-3 py-1 text-sm ${layout === "list" ? "bg-accent text-bg" : "bg-bg text-text"}`}
            data-testid="reservations-layout-list"
          >
            {t("admin.today.layout.list")}
          </button>
          <button
            type="button"
            aria-pressed={layout === "timeline"}
            onClick={() => setLayout("timeline")}
            className={`px-3 py-1 text-sm ${layout === "timeline" ? "bg-accent text-bg" : "bg-bg text-text"}`}
            data-testid="reservations-layout-timeline"
          >
            {t("admin.today.layout.timeline")}
          </button>
        </div>

        {layout === "list" && (
          <div className="flex gap-2 flex-1 justify-end flex-wrap">
            <input
              type="search"
              placeholder={t("admin.reservations.search.placeholder")}
              value={search}
              onChange={(e) => setListParams({ search: e.target.value })}
              className="field"
              data-testid="reservations-search"
            />
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="btn btn-outline"
              data-testid="reservations-filter-button"
            >
              {t("admin.reservations.filters.title")}
            </button>
            {/* Phase 6 plan 06-04 (EXP-01..03): admin-only CSV/PDF
                export. The current selected date (or last-week range
                when a search is active — search drops the date
                bounds, so a single-day export still makes sense) is
                the default export window. Operators can pre-fill
                `from`/`to` later; for v1 the chosen date is the
                single-day range. */}
            {searchActive ? null : (
              <ExportButton fromDate={date} toDate={date} />
            )}
          </div>
        )}
      </div>

      {layout === "list" ? (
        <>
          <TodayListView
            date={date}
            items={listViewItems}
            sortKey={sortKey}
            onSortChange={setSortKey}
            manualCreateHref={manualCreateHref}
            renderItem={(item) => (
              <ReservationRowActions
                item={item}
                onCancel={onCancel}
                onConfirm={onConfirm}
                onMarkArrived={onMarkArrived}
                onMarkNoShow={onMarkNoShow}
                isCancelling={pendingCancel === item.id}
                isConfirming={pendingConfirm === item.id}
                isMarkingArrived={pendingArrived === item.id}
                isMarkingNoShow={pendingNoShow === item.id}
                {...(hasExplicitFilter
                  ? {
                      returnFilter: {
                        date,
                        search,
                        status: filter.status,
                        source: filter.source,
                      },
                    }
                  : {})}
              />
            )}
          />
          {listViewItems.length === 0 && !query.isLoading && (
            <p
              className="text-text/60 text-center py-8"
              data-testid="reservations-empty"
            >
              {t("admin.reservations.empty")}
            </p>
          )}
          {query.hasNextPage && (
            <div className="flex justify-center mt-4">
              <button
                type="button"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                className="btn btn-outline"
                data-testid="reservations-load-more"
              >
                {t("admin.reservations.loadMore")}
              </button>
            </div>
          )}
        </>
      ) : (
        <TodayTimelineView items={timelineQuery.data ?? []} />
      )}

      <ReservationFilters
        open={showFilters}
        value={filter}
        onApply={(next) =>
          setListParams({ status: next.status, source: next.source })
        }
        onClear={() => setListParams({ status: [], source: [] })}
        onClose={() => setShowFilters(false)}
      />

      {cancelMutation.error && (
        <p
          role="alert"
          className="text-sm text-error mt-2"
          data-testid="cancel-error"
        >
          {cancelMutation.error instanceof ClientError
            ? (cancelMutation.error.response.errors?.[0]?.message ?? "Error")
            : "Error"}
        </p>
      )}
      {confirmMutation.error && (
        <p
          role="alert"
          className="text-sm text-error mt-2"
          data-testid="confirm-error"
        >
          {confirmMutation.error instanceof ClientError
            ? (confirmMutation.error.response.errors?.[0]?.message ?? "Error")
            : "Error"}
        </p>
      )}
      {markArrivedMutation.error && (
        <p
          role="alert"
          className="text-sm text-error mt-2"
          data-testid="mark-arrived-error"
        >
          {markArrivedMutation.error instanceof ClientError
            ? (markArrivedMutation.error.response.errors?.[0]?.message ??
              "Error")
            : "Error"}
        </p>
      )}
      {markNoShowMutation.error && (
        <p
          role="alert"
          className="text-sm text-error mt-2"
          data-testid="mark-no-show-error"
        >
          {markNoShowMutation.error instanceof ClientError
            ? (markNoShowMutation.error.response.errors?.[0]?.message ??
              "Error")
            : "Error"}
        </p>
      )}

      {blockDayDialog && (
        <CalendarDayTapDialog
          date={blockDayDialog.date}
          dayState={blockDayDialog.dayState}
          onClose={() => setBlockDayDialog(null)}
        />
      )}
    </div>
  );
}
